import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {CardContent, CardActionArea} from 'views/components';
import {Typography, Stack, Divider, Box, Chip} from 'views/components';
import {Place, CalendarToday, Timer, Schedule, PersonOutline, TrendingUpOutlined} from 'views/icons';
import {translate, Translate, LongStandardDateText} from 'views/module';

import contrast from 'css/Contrast';

import moment from 'moment';

const Card = (props) => {
  const item = props.item;

  const nullChar = '-';

  const clientCareplan = item.clientCarePlan;
  const clientForm = item.clientForm ?? [];
  const clientStatusLog = item.clientLastLogStatusUpdate;
  const clientLastMessage = item.clientLastMessage;

  // Fetch forms
  const clientScheduleIntakeForm = Object.values(clientForm).find((obj) => obj.formKey === 'CLIENT_SCHEDULE_INTAKE_FORM');

  const clientRn = item.clientRn;
  const clientName = item.clientName;
  const clientCreatedAt = item.createdAt;
  const status = item.status;
  const callStatus = item.clientCallStatus?.introCallStatus;
  const clientLocation = clientCareplan?.generalInformation?.address ?? nullChar;
  const clientRelationships = React.useMemo(() => {
    const relationships = Object.values(item.relationships ?? {});
    if (!relationships.length) return [];
    return relationships.slice().sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority;
      return a.name.localeCompare(b.name);
    });
  }, [item.relationships]);

  // Fetch client data
  const clientDaysInStage = React.useMemo(() => {
    if (!clientStatusLog) {
      return Math.floor(moment().diff(moment(clientCreatedAt), 'days', true));
    } else {
      return Math.floor(moment().diff(moment(clientStatusLog.createdAt), 'days', true));
    }
  }, [clientStatusLog, clientCreatedAt]);

  const clientDaysInStageText = React.useMemo(() => {
    return translate(
        `client.ClientDetail.Status.SubtitleValue.${clientDaysInStage === 1 ? 'Day' : 'Days'}`,
        {number: clientDaysInStage},
    );
  }, [clientDaysInStage]);

  const clientDaysSinceLastContact = React.useMemo(() => {
    if (!clientLastMessage) return null;
    return Math.floor(moment().diff(moment(clientLastMessage.createdAt), 'days', true));
  }, [clientLastMessage]);

  const clientDaysSinceLastContactText = React.useMemo(() => {
    if (clientDaysSinceLastContact === null) return null;
    return translate(
        `client.ClientDetail.Status.SubtitleValue.${clientDaysSinceLastContact === 1 ? 'Day' : 'Days'}`,
        {number: clientDaysSinceLastContact},
    );
  }, [clientDaysSinceLastContact]);

  const clientStartTimeline = React.useMemo(() => {
    const startOptions = {
      'two-weeks': translate('client.ClientDetail.Status.SubtitleValue.TwoWeeks'),
      'four-weeks': translate('client.ClientDetail.Status.SubtitleValue.FourWeeks'),
      'unsure': nullChar,
    };
    return startOptions[clientScheduleIntakeForm?.answers['Schedule-start-time']?.value?.selectedOptionValue] ?? nullChar;
  }, [clientScheduleIntakeForm]);

  const clientHrsPerWeek = React.useMemo(() => {
    const hrsOptions = {
      '1-hour': 1,
      '2-3-hours': [2, 3],
      '4-6-hours': [4, 6],
      '8-12-hours': [8, 12],
      'other': translate('client.ClientDetail.Status.SubtitleValue.Other'),
    };

    const daysPerWeek = clientScheduleIntakeForm?.answers['Schedule-days']?.value?.selectedOptionValues?.length;
    const selectedHrsPerDay = clientScheduleIntakeForm?.answers['Schedule-visit-duration']?.value?.selectedOptionValue;
    const hrsPerDay = hrsOptions[selectedHrsPerDay] ?? nullChar;
    if (typeof(hrsPerDay) !== 'string') {
      if (hrsPerDay.length > 1) {
        return hrsPerDay && daysPerWeek ? `${hrsPerDay[0] * daysPerWeek}-${hrsPerDay[1] * daysPerWeek}` : nullChar;
      } else {
        return hrsPerDay && daysPerWeek ? hrsPerDay * daysPerWeek : nullChar;
      }
    } else {
      return hrsPerDay;
    }
  }, [clientScheduleIntakeForm]);

  const primaryRelationship = React.useMemo(() => {
    if (clientRelationships[0]?.name) {
      return clientRelationships[0];
    } else {
      return {name: nullChar, connectionType: nullChar};
    }
  }, [clientRelationships, nullChar]);

  const clientMatch = React.useMemo(() => {
    return Object.keys(translate('client.MatchDifficulty')).includes(clientCareplan?.caregiverRequirements?.matchDifficulty) ?
    clientCareplan?.caregiverRequirements?.matchDifficulty : nullChar;
  }, [clientCareplan]);


  // Chip builder
  const statusChip = React.useMemo(() => {
    const title = translate(`client.Status.${status}.Title`);
    const color = translate(`client.Status.${status}.Color`);
    const textColor = contrast(color);
    return (
      <Chip
        label={title}
        sx={{
          backgroundColor: color,
          color: textColor,
          width: 'max-content',
          px: '.25rem',
        }}
        size='small'
      />
    );
  }, [status]);

  const callStatusChip = React.useMemo(() => {
    if (!callStatus) return;
    const title = translate(`client.CallStatus.${callStatus}.Title`);
    const color = translate(`client.CallStatus.${callStatus}.Color`);
    const textColor = contrast('transparent');
    return (
      <Chip
        label={title}
        sx={{
          backgroundColor: color,
          color: color? textColor: 'inherit',
          width: 'max-content',
          px: '.25rem',
        }}
        size='small'
      />
    );
  }, [callStatus]);

  return (
    <React.Fragment>
      <CardActionArea
        sx={{
          textAlign: 'left',
        }}
        to={`/client/${clientRn}`}
      >
        <CardContent>
          {/* Card header */}
          <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
            {/* Left side */}
            <Stack gap={.5}>
              <Typography variant="h6" component="div">
                {clientName}
              </Typography>
              <Typography variant="body2" color="textSecondary" component="div">
                <Place fontSize='1rem' sx={{mr: 1}} />
                {clientLocation}
              </Typography>
            </Stack>
            {/* Right side */}
            <Stack gap={1} sx={{alignItems: 'flex-end'}}>
              {statusChip}
              {callStatusChip}
            </Stack>
          </Box>
          <Divider sx={{mt: 1, mb: 1}}/>
          {/* Card info */}
          <Box
            display="grid"
            sx={{
              'flex': 1,
              'gap': 1,
              'gridTemplateColumns': {
                xs: 'repeat(2, minmax(8rem, 1fr))',
                sm: 'repeat(2, minmax(8rem, 1fr))',
                md: 'repeat(3, minmax(8rem, 1fr))',
              },
            }}
          >
            {/* Created at */}
            <Box component="span">
              <Stack direction="row" sx={{alignItems: 'center'}}>
                <CalendarToday fontSize='.5rem' sx={{mr: 1}} />
                <Typography variant="body2" color="textSecondary" component="div">
                  <Translate expression="client.ClientDetail.Status.Subtitles.Created" />
                </Typography>
              </Stack>
              <Typography variant="body2" color="textPrimary" sx={{ml: 3, wordBreak: 'break-word'}} component="div">
                {
                  clientCreatedAt ?
                  <LongStandardDateText date={clientCreatedAt} /> : nullChar
                }
              </Typography>
            </Box>
            {/* Days */}
            <Box component="span">
              <Stack direction="row" sx={{alignItems: 'center'}}>
                <Timer fontSize='.5rem' sx={{mr: 1}} />
                <Typography variant="body2" color="textSecondary" component="div">
                  <Translate expression="client.ClientDetail.Status.Subtitles.Days" />
                </Typography>
              </Stack>
              <Stack sx={{ml: 3, wordBreak: 'break-word'}}>
                <Typography variant="body2" color="textPrimary" component="div">
                  <Translate
                    expression="client.ClientDetail.Status.Subtitles.InStage"
                    substitute={{value: clientDaysInStageText}}
                  />
                </Typography>
                {
                  clientLastMessage &&
                  <Typography variant="body2" color="textPrimary" component="div">
                    <Translate
                      expression="client.ClientDetail.Status.Subtitles.SinceLastMsg"
                      substitute={{value: clientDaysSinceLastContactText}}
                    />
                  </Typography>
                }
              </Stack>
            </Box>
            {/* Start Timeline */}
            <Box component="span">
              <Stack direction="row" sx={{alignItems: 'center'}}>
                <Schedule fontSize='1rem' sx={{mr: 1}} />
                <Typography variant="body2" color="textSecondary" component="div">
                  <Translate expression="client.ClientDetail.Status.Subtitles.StartTimeline" />
                </Typography>
              </Stack>
              <Typography variant="body2" color="textPrimary" sx={{ml: 3, wordBreak: 'break-word'}} component="div">
                {clientStartTimeline}
              </Typography>
            </Box>
            {/* Hrs/Week */}
            <Box component="span">
              <Stack direction="row" sx={{alignItems: 'center'}}>
                <Schedule fontSize='1rem' sx={{mr: 1}} />
                <Typography variant="body2" color="textSecondary" component="div">
                  <Translate expression="client.ClientDetail.Status.Subtitles.HrsPerWeek" />
                </Typography>
              </Stack>
              <Typography variant="body2" color="textPrimary" sx={{ml: 3, wordBreak: 'break-word'}} component="div">
                {clientHrsPerWeek}
              </Typography>
            </Box>
            {/* Care Recipient */}
            <Box component="span">
              <Stack direction="row" sx={{alignItems: 'center'}}>
                <PersonOutline fontSize='1rem' sx={{mr: 1}} />
                <Typography variant="body2" color="textSecondary" component="div">
                  <Translate expression="client.ClientDetail.Status.Subtitles.CareRelationship" />
                </Typography>
              </Stack>
              <Typography variant="body2" color="textPrimary" sx={{ml: 3, wordBreak: 'break-word'}} component="div">
                <>
                  {(() => {
                    const maxChars = 23;
                    return primaryRelationship.name.length > maxChars ? (primaryRelationship.name.slice(0, maxChars) + '...') : primaryRelationship.name;
                  })()}
                  {
                    <Typography variant="body2" color="textSecondary" component="span" sx={{ml: .5, wordBreak: 'keep-all', flexShrink: 0}}>
                      ({primaryRelationship.connectionType})
                    </Typography>
                  }
                </>
              </Typography>
            </Box>
            {/* Match Difficulty */}
            <Box component="span">
              <Stack direction="row" sx={{alignItems: 'center'}}>
                <TrendingUpOutlined fontSize='1rem' sx={{mr: 1}} />
                <Typography variant="body2" color="textSecondary" component="div">
                  <Translate expression="client.ClientDetail.Status.Subtitles.MatchDifficulty" />
                </Typography>
              </Stack>
              <Typography variant="body1" color="textPrimary" sx={{ml: 3, wordBreak: 'break-word'}} component="div">
                {(() => {
                  if (clientMatch != nullChar) {
                    return (
                      <Chip
                        label={<Translate expression={`client.MatchDifficulty.${clientMatch}.Title`} />}
                        color={translate(`client.MatchDifficulty.${clientMatch}.Color`)}
                        variant="outlined"
                        size='small'
                      />
                    );
                  } else {
                    return clientMatch;
                  }
                })()}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </React.Fragment>
  );
};

Card.propTypes = {
  item: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(Card));
